// @flow
import { XCircle } from '@phosphor-icons/react'
import usePermissions from 'hooks/usePermissions'
import { isNil } from 'lodash'
import has from 'lodash/has'
import * as React from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import type { FormWithPdfApiResponse } from 'types/form-with-pdf-api-response'
import { isFilledArray } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'

type Props = {
  forms: Array<FormWithPdfApiResponse>,
  packetName: string,
  onRemoveAll: () => void,
  onRemove: (string) => void,
  onReorder: (Object) => void,
}

function CreateUpdatePacketItemList({
  forms,
  packetName,
  onRemoveAll,
  onReorder,
  onRemove,
}: Props): React.Node {
  const { canEditPacketForm } = usePermissions()

  const reorder = React.useCallback(
    (result) => {
      if (
        !has(result, ['source', 'index']) ||
        !has(result, ['destination', 'index']) ||
        result.source.index === result.destination?.index
      ) {
        return
      }
      onReorder(result)
    },
    [onReorder]
  )

  return (
    <div
      className={cn(
        'relative ml-2 flex flex-shrink-0 flex-col rounded-md border bg-white shadow-sm',
        { 'w-1/2': canEditPacketForm, 'w-full': !canEditPacketForm }
      )}
    >
      <div className="flex w-full flex-shrink-0 justify-between rounded-t-md border-b border-gray-200 bg-gray-50 px-3 py-3 text-left font-medium text-gray-900">
        {packetName || 'New Packet'}
        {forms.length > 0 && canEditPacketForm && (
          <button onClick={onRemoveAll}>
            <XCircle className="text-lg text-gray-500" weight="bold" />
          </button>
        )}
      </div>
      <DragDropContext onDragEnd={reorder}>
        <Droppable droppableId="EDIT_PACKET_FORMS_LIST ">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="overflow-y-auto"
            >
              {isFilledArray(forms) &&
                forms.map((item, index) => {
                  if (isNil(item?.id) || isNil(item?.name)) return null
                  return (
                    <Draggable
                      draggableId={item.id}
                      index={index}
                      isDragDisabled={!canEditPacketForm}
                      key={item.id}
                    >
                      {(dragProvided) => (
                        <div
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className="flex flex-shrink-0 items-center border-b border-gray-200 bg-white px-3 py-2.5"
                          ref={dragProvided.innerRef}
                        >
                          <p className="flex flex-1 pr-3 text-left text-sm leading-5 text-gray-800">
                            {item.name}
                          </p>
                          {canEditPacketForm && (
                            <button onClick={() => onRemove(item.id)}>
                              <XCircle className="text-xl text-gray-500/80" />
                            </button>
                          )}
                        </div>
                      )}
                    </Draggable>
                  )
                })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default CreateUpdatePacketItemList
